import {
  ChangeEvent,
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTheme } from 'styled-components';

import { unmask } from '~/common/utils/unmask';
import { inter } from '~/common/styles/fonts';
import { useToast } from '~/common/providers/toast';

import { ITextInputProps } from './types';
import {
  Container,
  InputWrapper,
  CustomInput,
  CounterWrapper,
  PrefixOrSuffixWrapper,
  CopySuffixWrapper,
  getTextInputHeight,
  LoaderWrapper,
} from './styles';
import { CurrencyInput } from './CurrencyInput';
import { HelperText } from '../HelperText';
import { FormLabel } from '../FormLabel';
import { Typography } from '../../Typography';
import { Loader } from '../../Loader';
import { Icon } from '../../Icons';

const BaseTextInput: ForwardRefRenderFunction<
  HTMLInputElement,
  ITextInputProps
> = (
  {
    style,
    label,
    size = 'medium',
    width = '300px',
    error,
    isDisabled,
    helperText,
    leftCustomIcon,
    rightCustomIcon,
    leftIcon,
    rightIcon,
    as = 'input',
    maxLength,
    _prefix,
    withoutPrefixDelimiter = false,
    _suffix,
    centralize = false,
    withCopySuffix = false,
    withoutBorder = false,
    withOnlyBorderBottom = false,
    loading = false,
    currency12digits = false,
    loader = 'clip',
    mask,
    onChange,
    onCustomCurrencyChange,
    ...props
  },
  ref
) => {
  const { showToast } = useToast();
  const id = useId();
  const theme = useTheme();
  const [currentCount, setCurrentCount] = useState(0);
  const [focus, setFocus] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Expõe o ref interno para o pai, se a ref externa for passada
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const inputType =
    props?.type === 'password'
      ? showPassword
        ? 'text'
        : 'password'
      : props.type;

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;

    if (maxLength > 0 && as === 'textarea') {
      const currentLength = value.length;
      if (currentLength > maxLength) {
        return;
      }

      setCurrentCount(currentLength);
    }

    if (mask) {
      Object.assign(event.target, {
        value: unmask(event.target.value),
      });
    }

    onChange?.(event);
  };

  const copyValue = (): void => {
    navigator.clipboard.writeText(
      (props.value as string) || inputRef.current?.value
    );

    showToast({
      type: 'success',
      position: 'top',
      description: 'O texto foi copiado para sua área de transferência!',
    });
  };

  const $hasPrefixOrSuffix = useMemo(
    () => !!_prefix || !!_suffix || withCopySuffix,
    [_prefix, _suffix, withCopySuffix]
  );

  const getTextAreaHeight = useCallback(() => {
    if (as === 'input') {
      return null;
    }

    if (label) {
      return `calc(${style.minHeight} - 24px)`;
    }

    return String(style.minHeight);
  }, [as, label, style]);

  return (
    <Container htmlFor={id} style={style}>
      {!!label && <FormLabel {...label} isDisabled={isDisabled} />}

      <InputWrapper
        $hasError={!!error}
        $isDisabled={isDisabled}
        $size={size}
        $width={width}
        $hasPrefixOrSuffix={$hasPrefixOrSuffix}
        $isPassword={inputType === 'password' || showPassword}
        $withoutBorder={withoutBorder}
        $withOnlyBorderBottom={withOnlyBorderBottom}
        $inputAs={as}
        $textAreaHeight={getTextAreaHeight()}
      >
        {_prefix && (
          <PrefixOrSuffixWrapper
            $focus={focus}
            $size={size}
            $borderPosition="right"
            $withoutPrefixDelimiter={withoutPrefixDelimiter}
          >
            {_prefix}
          </PrefixOrSuffixWrapper>
        )}

        {leftCustomIcon ? (
          <>{leftCustomIcon}</>
        ) : leftIcon ? (
          <Icon
            id="input-icon-id"
            type={leftIcon}
            size={20}
            color={isDisabled ? theme.neutral[600] : theme.neutral[500]}
          />
        ) : null}

        {currency12digits ? (
          <CurrencyInput
            name={props.name}
            value={props.value as number}
            onChange={onCustomCurrencyChange}
          />
        ) : (
          <CustomInput
            textAreaHeight={as === 'textarea' ? String(style.minHeight) : null}
            centralize={centralize}
            as={as}
            disabled={isDisabled}
            id={id}
            ref={inputRef}
            maxLength={maxLength}
            {...props}
            type={inputType}
            className={inter.className}
            onChange={handleChange}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            {...(props.value &&
              mask && {
                value: mask(props.value as string),
              })}
            {...($hasPrefixOrSuffix &&
              !!leftIcon && {
                style: {
                  paddingLeft: '0 !important',
                },
              })}
            {...($hasPrefixOrSuffix &&
              !!rightIcon && {
                style: {
                  paddingRight: '0 !important',
                },
              })}
          />
        )}

        {rightCustomIcon ? (
          <>{rightCustomIcon}</>
        ) : rightIcon ? (
          <Icon
            id="input-icon-id"
            type={rightIcon}
            size={20}
            color={isDisabled ? theme.neutral[600] : theme.neutral[500]}
          />
        ) : null}

        {loading && (
          <LoaderWrapper>
            <Loader
              size={Number(getTextInputHeight[size].replace('px', ''))}
              type={loader}
              color="neutral.50"
            />
          </LoaderWrapper>
        )}

        {props?.type === 'password' && (
          <CopySuffixWrapper
            $focus={focus}
            $size={size}
            onClick={() => setShowPassword((prev) => !prev)}
            style={{ cursor: 'pointer' }}
          >
            <Icon
              type={showPassword ? 'eye-line' : 'eye-off-line'}
              color={theme.neutral[400]}
              size={20}
            />
          </CopySuffixWrapper>
        )}

        {maxLength && as === 'textarea' && (
          <CounterWrapper $size={size}>
            <Typography.Subheading size="2x-small" color="neutral.500">
              {currentCount}/{maxLength}
            </Typography.Subheading>
          </CounterWrapper>
        )}

        {_suffix && (
          <PrefixOrSuffixWrapper
            $focus={focus}
            $size={size}
            $borderPosition="left"
            $withoutPrefixDelimiter={withoutPrefixDelimiter}
          >
            {_suffix}
          </PrefixOrSuffixWrapper>
        )}

        {withCopySuffix && (
          <CopySuffixWrapper $focus={focus} $size={size} onClick={copyValue}>
            <Icon type="file-copy-line" color={theme.neutral[400]} size={20} />
          </CopySuffixWrapper>
        )}
      </InputWrapper>

      {(!!helperText || !!error) && (
        <HelperText isDisabled={isDisabled} isError={!!error}>
          {error || helperText}
        </HelperText>
      )}
    </Container>
  );
};

export const TextInput = forwardRef(BaseTextInput);
